import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactGA from "react-ga4";
import { ArrowRight, Twitter, Instagram, Linkedin } from 'lucide-react';
import newLogo from '../assets/images/new-logo.png';

const CareersPage = () => {
  useEffect(() => {
    document.title = "Artie - Careers";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" onClick={() => ReactGA.event({ category: "Navigation", action: "Home", label: "Logo" })} className="flex items-center gap-2 group">
            <img src={newLogo} alt="Artie" className="w-40 h-40 object-contain" />
          </Link>
          <Link to="/" onClick={() => ReactGA.event({ category: "Navigation", action: "Home", label: "Back to Home" })} className="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors font-geist">
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-geist">Careers at Artie</h1>
        <p className="text-xl text-neutral-500 mb-12 font-geist leading-relaxed">
          We’re seeking passionate, thoughtful teammates excited to craft products creators choose to use around the world.
        </p>

        <div className="space-y-12 font-geist text-neutral-800 leading-relaxed">
          
          {/* How We Work */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">How We Work</h2>
            <p className="mb-4">
              We’re a remote-first, async-friendly team built around trust, autonomy, and clear ownership. Leaders spend real time with creators and other users to stay close to the work and grounded in real needs. Early on, decisions are made collaboratively, with structure and process added thoughtfully as we grow. Throughout, we optimize for long-term value and durable outcomes rather than quick wins.
            </p>
          </section>

          {/* Why Artie? */}
          <section>
            <h2 className="text-2xl font-bold mb-6 text-neutral-900">Why Artie?</h2>
            
            <div className="grid gap-6">
              <div>
                <h3 className="text-lg font-bold mb-1 text-neutral-900">Founding team with deep expertise</h3>
                <p className="text-neutral-600">15 years at Google/YouTube scaling creator monetization to billions in revenue</p>
              </div>
              
              <div>
                <h3 className="text-lg font-bold mb-1 text-neutral-900">Real market validation</h3>
                <p className="text-neutral-600">Active interest from F10 brands, Big5 ad agencies and major talent agencies</p>
              </div>

              <div>
                <h3 className="text-lg font-bold mb-1 text-neutral-900">Direct impact</h3>
                <p className="text-neutral-600">Help creators build sustainable businesses</p>
              </div>
            </div>
          </section>

          {/* Open Roles */}
          <section className="pt-8 border-t border-neutral-100">
            <h2 className="text-3xl font-bold mb-8 text-neutral-900">Open Roles</h2>
            
            <div className="bg-neutral-50 rounded-3xl p-8 border border-neutral-100 hover:border-neutral-200 transition-colors">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">Head of Engineering / Founding Engineer</h3>
                  <p className="text-neutral-500 font-medium">NYC/SF (preferred) / Remote friendly</p>
                </div>
                <div className="inline-flex px-3 py-1 bg-neutral-900 text-white text-xs font-bold uppercase tracking-wider rounded-full">
                  Full Time
                </div>
              </div>

              <div className="space-y-6 text-neutral-700">
                <p>
                  We are hiring a Founding Engineer / Head of Engineering to own Artie's technical foundation end-to-end. This role is the senior-most engineering leader at Artie and will define our engineering culture, architecture, and hiring from day one. This is about category creation, not feature execution.
                </p>
                <p>
                  This is a hands-on role first. You will write production code for the first 6–12 months, make core architectural decisions, and ship reliable systems. Over time, the role grows into full engineering leadership as the company scales. You will be a peer to the Head of Data Science. Both roles report to the CEO. Periodic travel to key industry moments (e.g., SXSW, Sundance, Cannes) will be required.
                </p>
                <p>
                  We're building a performance culture optimized for builders who care more about ownership, leverage, and long-term impact than short-term cash.
                </p>

                <div>
                  <h4 className="font-bold text-neutral-900 mb-3 text-lg">Key Responsibilities</h4>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Own all engineering across data infrastructure, backend systems, and model serving</li>
                    <li>Design architecture that supports a mixed AI stack (traditional ML, multi-model systems, agent-based components)</li>
                    <li>Build systems that directly drive commercial outcomes—your technical choices impact revenue</li>
                    <li>Integrate with external partner ecosystems (platforms, measurement vendors, brand APIs, agency systems)</li>
                    <li>Make and justify build vs. buy decisions across stages of company growth</li>
                    <li>Productionize models via inference services, versioned deployments, rollbacks, and monitoring</li>
                    <li>Build reliable data pipelines (APIs, storage, schema conformity)</li>
                    <li>Partner closely with Data Science on production reliability and system robustness</li>
                    <li>Debug and fix real production issues when they arise</li>
                    <li>Hire and mentor the first 2–3 engineers over time</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-neutral-900 mb-3 text-lg">What Success Looks Like</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-neutral-900 mb-1">First 6 months</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Reliable, observable model-serving infrastructure in production</li>
                        <li>Clear architectural decisions made and documented (including intentional technical debt)</li>
                        <li>Strong collaboration with Data Science and founders</li>
                        <li>Initial partner integrations live and operational</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900 mb-1">By 12 months</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>A small, high-functioning engineering team</li>
                        <li>Systems that support real user scale and continued iteration</li>
                        <li>Technical infrastructure that directly enables revenue growth</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-neutral-900 mb-3 text-lg">What We're Looking For</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-neutral-900 mb-1">Required</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Passion for content, creators, and creative ecosystems</li>
                        <li>Experience building systems that directly drive revenue (ads, payments, marketplaces, commerce)</li>
                        <li>Track record integrating with external partner ecosystems via APIs and data sharing</li>
                        <li>Comfort owning business outcomes, not just technical delivery</li>
                        <li>Experience productizing ML systems (production experience {'>'} research)</li>
                        <li>Comfort partnering with Data Science as a peer, not a customer</li>
                        <li>Strong technical judgment under ambiguity</li>
                        <li>Clear, effective communication with technical and non-technical stakeholders</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900 mb-1">Preferred</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Experience building at early-stage companies and scaling systems from scrappy to production-grade</li>
                        <li>Background in data-heavy or API-driven platforms</li>
                        <li>Has hired and built small teams (2-5 engineers)</li>
                        <li>Familiarity with measurement/attribution systems</li>
                        <li>Experience working directly with non-technical partners (brands, agencies, talent)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-neutral-900 mb-3 text-lg">Compensation</h4>
                  <p className="text-xl font-medium text-neutral-900">$150K-$225K base + 3-5% equity</p>
                </div>

                <div className="pt-6">
                  <a href="mailto:careers@artie.co?subject=Application for Head of Engineering / Founding Engineer" 
                    onClick={() => {
                        // Optional: Add analytics event here
                        if ((window as any).gtag) {
                            (window as any).gtag('event', 'click', {
                                'event_category': 'Careers',
                                'event_label': 'Apply - Head of Engineering'
                            });
                        }
                    }}
                    className="inline-flex items-center justify-center h-12 px-8 text-base font-medium text-white transition-colors bg-neutral-900 rounded-full hover:bg-neutral-800 font-geist shadow-md hover:shadow-lg"
                  >
                    Apply Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Don't See the Right Role Yet? */}
          <section className="pt-12 border-t border-neutral-100">
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Don't See the Right Role Yet?</h2>
            <p className="mb-6">
              If you're excited about what we're building but don't see a role that fits, we still want to hear from you. Send us your resume and a short introduction covering why Artie feels like the right place for you and where you think you could have the most impact.
            </p>
            <p>
              To apply, or if you have questions, email us at <a href="mailto:careers@artie.co" className="text-neutral-900 font-medium underline underline-offset-2 hover:text-neutral-600">careers@artie.co</a>.
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white pt-12 pb-12 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
             <div className="flex gap-4">
              <a href="https://www.instagram.com/officialartieco?igsh=ZWQyYXNwNG8zN3lr" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/artieco/" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900 transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
            <p className="text-sm text-neutral-400 font-geist">
              © 2026 Artie. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;